/**
 * Resolves once the Clerk script the page already carries has run - the one
 * `scripts/clerk-early.mjs` puts into a built index.html - so the portal mounts
 * `ClerkProvider` AFTER `window.Clerk` exists rather than before it.
 *
 * Why it matters (portal speed, measured in Chrome on demo Internal with a real
 * Google session, 2026-10-23): the provider's loader (`loadClerkJsScript` in
 * @clerk/shared) checks for `window.Clerk` once and then POLLS every 100 ms. The
 * lazy Clerk chunk and the script arrive within a few milliseconds of each other,
 * so the first check usually missed and `Clerk.load()` - which is what asks
 * Clerk for the session - started 81 to 118 ms after the script had run, on
 * every load. Waiting on the script's own `load` event instead makes the
 * provider find Clerk ready and call `load()` at once.
 *
 * Resolves at once when Clerk is already there, or when the page carries no
 * such script (a development build: the provider then injects it itself). A
 * script that never fires (failed before this listened) is given up after
 * `timeoutMs`, and the provider's own loader - which detects and retries a
 * failed script - takes over exactly as before.
 */
export function clerkScriptReady(timeoutMs = 2000): Promise<void> {
  if (typeof document === 'undefined') return Promise.resolve();
  if ((globalThis as { Clerk?: unknown }).Clerk !== undefined) return Promise.resolve();
  const script = document.querySelector<HTMLScriptElement>('script[data-clerk-js-script]');
  if (script === null) return Promise.resolve();
  return new Promise((resolve) => {
    const done = (): void => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(done, timeoutMs);
    script.addEventListener('load', done, { once: true });
    script.addEventListener('error', done, { once: true });
  });
}
